'use client';

import Image, { StaticImageData } from 'next/image';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
  Variants,
} from 'framer-motion';
import { useState, useRef, PointerEvent } from 'react';
import Link from 'next/link';

interface HoverShowMoreCardProps
{
  src: string | StaticImageData;
  alt?: string;
  sharedClasses?: string;
}

/**
 * A hypnotic “show-more” card for your gallery:
 * – 3-D parallax tilt that tracks the cursor
 * – twin neon blobs that chase the cursor and fade smoothly
 * – “Více…” label springs to the centre and does a gentle perpetual wiggle *only while hovered*
 */
export default function HoverShowMoreCard({
  src,
  alt = 'ukázka práce',
  sharedClasses = '',
}: HoverShowMoreCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);

  /* -------------------------------------------------- *
   *  1 . cursor-based motion values
   * -------------------------------------------------- */
  const mvX = useMotionValue(0.5); // 0 = left, 1 = right
  const mvY = useMotionValue(0.5); // 0 = top, 1 = bottom

  /*  Tilt: rotate in opposite directions for X/Y */
  const rotateX = useSpring(useTransform(mvY, [0, 1], [14, -14]), {
    stiffness: 300,
    damping: 35,
  });
  const rotateY = useSpring(useTransform(mvX, [0, 1], [-14, 14]), {
    stiffness: 300,
    damping: 35,
  });
  const scale = useSpring(1, { stiffness: 260, damping: 30 });

  /* -------------------------------------------------- *
   *  2 . neon gradient that CHASES the cursor
   * -------------------------------------------------- */
  const pX = useTransform(mvX, [0, 1], [0, 100]);
  const pY = useTransform(mvY, [0, 1], [0, 100]);
  const pXinv = useTransform(mvX, [0, 1], [100, 0]);
  const pYinv = useTransform(mvY, [0, 1], [100, 0]);

  const gradient = useMotionTemplate`
    radial-gradient(circle at ${ pX }% ${ pY }%, rgba(255,0,150,0.45), transparent 60%),
    radial-gradient(circle at ${ pXinv }% ${ pYinv }%, rgba(0,200,255,0.40), transparent 60%)
  `;

  /* -------------------------------------------------- *
   *  3 . “Více…” label variants
   * -------------------------------------------------- */
  const labelVariants: Variants = {
    rest: { x: '0%', y: '0%', scale: 1, rotate: 0 },
    hover: {
      x: '25%',
      y: '-200%',
      scale: 1.4,
      rotate: [0, 4, -4, 0],
      transition: {
        rotate: { repeat: Infinity, duration: 1.8, ease: 'easeInOut' },
        x: { duration: 0.4 },
        y: { duration: 0.4 },
        scale: { duration: 0.4 },
      },
    },
  };

  /* -------------------------------------------------- *
   *  4 . pointer handlers
   * -------------------------------------------------- */
  const handleMove = (e: PointerEvent<HTMLDivElement>) => {
    const b = cardRef.current?.getBoundingClientRect();
    if (!b)
    {
      return;
    }
    mvX.set((e.clientX - b.left) / b.width);
    mvY.set((e.clientY - b.top) / b.height);
  };

  const handleEnter = () => {
    setHovered(true);
    scale.set(1.08);
  };

  const handleLeave = () => {
    setHovered(false);
    scale.set(1);
    mvX.set(0.5);
    mvY.set(0.5);
  };

  /* -------------------------------------------------- *
   *  5 . render
   * -------------------------------------------------- */
  return (
    <Link href='/galerie'>
      <motion.div
        ref={ cardRef }
        className='relative grid grid-cols-1 grid-rows-1 items-end cursor-pointer select-none'
        style={ { perspective: 1200 } }
        onPointerMove={ handleMove }
        onPointerEnter={ handleEnter }
        onPointerLeave={ handleLeave }
      >
        {/* --- IMAGE + NEON OVERLAY LAYER ----------------------------------- */ }
        <motion.div
          className='row-start-1 row-end-2 col-start-1 col-end-2 w-full h-full overflow-hidden rounded-xl shadow-xl'
          style={ { rotateX, rotateY, scale, transformStyle: 'preserve-3d' } }
        >
          <Image
            src={ src }
            alt={ alt }
            width={ 900 }
            height={ 1600 }
            sizes='(max-width: 768px) 50vw, 20vw'
            className={ `${ sharedClasses } w-full h-full object-cover pointer-events-none brightness-75` }
            draggable={ false }
          />

          {/* — dynamic neon — */ }
          <motion.div
            className='absolute inset-0 pointer-events-none mix-blend-screen'
            style={ {
              backgroundImage: gradient,
              opacity: hovered ? 1 : 0,
              transition: 'opacity 0.35s ease',
            } }
          />
        </motion.div>

        {/* --- “Více…” ------------------------------------------------------ */ }
        <motion.span
          className='row-start-1 row-end-2 col-start-1 col-end-2 z-40 text-2xl md:text-4xl font-bold text-white pl-4 pb-2 origin-bottom-left'
          variants={ labelVariants }
          initial='rest'
          animate={ hovered ? 'hover' : 'rest' }
        >
          Více...
        </motion.span>
      </motion.div>
    </Link>
  )
}
